import { Box, Divider as MantineDivider, DividerProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedDividerProps extends Omit<DividerProps, 'variant'> {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  animated?: boolean;
  thickness?: number;
}

const Divider = forwardRef<HTMLDivElement, ThemedDividerProps>(
  (
    {
      gradient = false,
      gradientVariant = 'primary',
      animated = false,
      thickness = 1,
      orientation = 'horizontal',
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();

    if (!gradient) {
      return <MantineDivider ref={ref} orientation={orientation} {...rest} />;
    }

    const gradientConfig = theme.other.gradients[gradientVariant];
    const isHorizontal = orientation === 'horizontal';

    return (
      <Box
        ref={ref}
        style={{
          width: isHorizontal ? '100%' : thickness,
          height: isHorizontal ? thickness : '100%',
          background: `linear-gradient(${isHorizontal ? 90 : 180}deg, ${gradientConfig.from}, ${gradientConfig.to})`,
          backgroundSize: animated ? '200% 100%' : '100%',
          animation: animated ? 'divider-shift 3s ease infinite' : undefined,
          borderRadius: thickness / 2,
        }}
      >
        {animated && (
          <style>{`
            @keyframes divider-shift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
        )}
      </Box>
    );
  }
);

Divider.displayName = 'Divider';

export default Divider;
